const KEY = "jic_jwt";
const EMBEDDED_KEY = "jic_embedded_user";
const JIC_URL = "https://u.jic.io";
const SYNC_AFTER_CABIN_KEY = "jic_sync_after_cabin";

export type JicUser = {
  sub: string;
  type?: "human" | "agent";
  owner?: string;
  exp?: number;
  source?: "u-jic" | "embedded";
};

declare global {
  interface Window {
    JIC?: {
      token: () => string | null;
      user: () => JicUser | null;
      loggedIn: () => boolean;
      login: () => void;
      logout: () => void;
    };
  }
}

function decode(token: string): JicUser | null {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)) as JicUser;
  } catch {
    return null;
  }
}

export function getJicUser(): JicUser | null {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem(KEY);
  if (token) {
    const user = decode(token);
    if (user && (!user.exp || user.exp * 1000 > Date.now())) {
      return { ...user, source: "u-jic" };
    }
  }

  return getEmbeddedJicUser();
}

// 仅 u-jic 登录才有 JWT;embedded(纯钱包)身份拿不到,需要先走 loginJic()
export function getJicToken(): string | null {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem(KEY);
  if (!token) return null;
  const user = decode(token);
  if (!user || (user.exp && user.exp * 1000 <= Date.now())) return null;
  return token;
}

export function getJicAddress(): string | null {
  const user = getJicUser();
  return user?.sub ? user.sub.toLowerCase() : null;
}

function dispatchJicChange() {
  window.dispatchEvent(new Event("jic:auth"));
}

function normalizeAddress(address: string) {
  const trimmed = address.trim();
  return trimmed.startsWith("0x") ? trimmed.toLowerCase() : trimmed;
}

function getEmbeddedJicUser(): JicUser | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(EMBEDDED_KEY);
  if (!raw) return null;
  try {
    const user = JSON.parse(raw) as JicUser;
    if (!user.sub) return null;
    return { ...user, sub: normalizeAddress(user.sub), source: "embedded" };
  } catch {
    return null;
  }
}

export function loginJicEmbedded(address: string) {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY);
  localStorage.setItem(
    EMBEDDED_KEY,
    JSON.stringify({
      sub: normalizeAddress(address),
      type: "human",
      source: "embedded",
    } satisfies JicUser)
  );
  dispatchJicChange();
}

export function loginJic({ clearLocal = false }: { clearLocal?: boolean } = {}) {
  if (typeof window === "undefined") return;
  if (clearLocal) {
    localStorage.removeItem(KEY);
    localStorage.removeItem(EMBEDDED_KEY);
    dispatchJicChange();
  }
  const redirectUrl = new URL(window.location.href.split("#")[0].split("?")[0]);
  redirectUrl.searchParams.set("jic_auth", "1");
  const redirect = redirectUrl.toString();
  window.location.href =
    `${JIC_URL}/login?redirect_uri=${encodeURIComponent(redirect)}` +
    `&site=${encodeURIComponent(window.location.host)}`;
}

function clearLocalJic() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY);
  localStorage.removeItem(EMBEDDED_KEY);
  dispatchJicChange();
}

export function clearJicSession() {
  clearLocalJic();
}

function openRemoteLogoutWindow(): Window | null {
  if (typeof window === "undefined") return null;
  return window.open(
    `${JIC_URL}/logout`,
    "jic_logout",
    "popup,width=440,height=560,left=80,top=80"
  );
}

export function logoutJic() {
  clearLocalJic();
  const logoutWindow = openRemoteLogoutWindow();
  if (logoutWindow) {
    window.setTimeout(() => logoutWindow.close(), 1800);
  } else if (typeof window !== "undefined") {
    window.location.href = `${JIC_URL}/logout`;
  }
}

export function openJicCabin() {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SYNC_AFTER_CABIN_KEY, "1");
  window.location.href = `${JIC_URL}/u`;
}

export function syncJicAfterCabinIfNeeded() {
  if (typeof window === "undefined") return false;
  if (sessionStorage.getItem(SYNC_AFTER_CABIN_KEY) !== "1") return false;
  sessionStorage.removeItem(SYNC_AFTER_CABIN_KEY);
  loginJic({ clearLocal: true });
  return true;
}

export async function exchangeJicCodeFromUrl(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get("jic_auth") !== "1") return false;
  const code = url.searchParams.get("code");
  if (!code) return false;

  try {
    const response = await fetch(`${JIC_URL}/auth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    const result = (await response.json()) as { jwt?: string };
    if (result.jwt) {
      localStorage.setItem(KEY, result.jwt);
      localStorage.removeItem(EMBEDDED_KEY);
      dispatchJicChange();
    }
  } catch {
    // Leave the page usable even if the exchange endpoint is temporarily down.
  } finally {
    url.searchParams.delete("code");
    url.searchParams.delete("jic_auth");
    window.history.replaceState({}, "", url.toString());
  }
  return true;
}

export function onJicChange(cb: (user: JicUser | null) => void): () => void {
  if (typeof window === "undefined") return () => {};
  let last = getJicAddress();
  cb(getJicUser());

  const emit = () => {
    const next = getJicAddress();
    if (next !== last) {
      last = next;
      cb(getJicUser());
    }
  };

  window.addEventListener("jic:auth", emit);
  window.addEventListener("storage", emit);
  window.addEventListener("focus", emit);
  const id = window.setInterval(emit, 2500);

  return () => {
    window.removeEventListener("jic:auth", emit);
    window.removeEventListener("storage", emit);
    window.removeEventListener("focus", emit);
    window.clearInterval(id);
  };
}

export async function fetchJicHandle(address: string): Promise<string | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/handle/${address}`);
    const data = (await response.json()) as { name?: string };
    return data.name || null;
  } catch {
    return null;
  }
}
